import React, { useState, useEffect } from 'react';
import { Outlet, NavLink as RouterNavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { House, Megaphone, Lightbulb, ChartBar, Pulse, Gear, SignOut, List, X, CaretLeft, CaretRight, User, Buildings, Calculator, BookOpen, Storefront, ChatCircleText } from '@phosphor-icons/react';
import { NavLink, ActionIcon, Tooltip, Menu, Text, Divider, Box, Badge, Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import Logo from './Logo';
import TaskNotifications from './TaskNotifications';
import Onboarding from './Onboarding';
import GlobalSearch from './GlobalSearch';
import AccountSwitcher from './AccountSwitcher';
import ErrorBoundary from './ErrorBoundary';
import LanguageSwitcher from './LanguageSwitcher';
import SupportModal from './SupportModal';
import api from '../utils/api';

export default function Layout() {
  const { t, i18n } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsedRaw] = useState(() => localStorage.getItem('sidebar_collapsed') === 'true');
  const setCollapsed = (v) => {
    setCollapsedRaw(v);
    localStorage.setItem('sidebar_collapsed', String(v));
  };
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [resumingSub, setResumingSub] = useState(false);

  // Pasek „subskrypcja wygasa": pokazujemy, gdy klient anulował subskrypcję (Stripe),
  // ale nadal jest ona aktywna do wyznaczonej daty. Pozwala jednym kliknięciem wznowić.
  const cancelAtRaw = user?.organization?.subscription_cancel_at;
  const cancelAtDate = cancelAtRaw ? new Date(cancelAtRaw) : null;
  const subEnding = !!(cancelAtDate && !isNaN(cancelAtDate) && cancelAtDate > new Date());
  const cancelAtLabel = cancelAtDate
    ? cancelAtDate.toLocaleDateString(({ en: 'en-GB', de: 'de-DE' })[i18n.language] || 'pl-PL',
        { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const handleResumeSubscription = async () => {
    setResumingSub(true);
    try {
      await api.post('/ads/subscription/resume/');
      await refreshUser();
      notifications.show({ message: t('subscription.resumed'), color: 'green' });
    } catch {
      notifications.show({ message: t('subscription.resumeError'), color: 'red' });
    } finally {
      setResumingSub(false);
    }
  };

  useEffect(() => {
    const onboardingComplete = localStorage.getItem('droid_onboarding_complete');
    if (onboardingComplete) return;

    api.get('/ads/campaigns/')
      .then(res => {
        const campaigns = res.data;
        if (!campaigns || campaigns.length === 0) {
          setShowOnboarding(true);
        }
      })
      .catch(() => {
        setShowOnboarding(true);
      });
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const isAgency = user?.organization?.is_agency;
  const hasBudgetPlanner = user?.organization?.budget_planner_enabled;
  // Szerokość treści: wąski (max-w-6xl), średni (szerszy, ale wyśrodkowany),
  // szeroki (pełna szerokość ekranu). Domyślnie wąski.
  const layoutMaxW = user?.pref_layout === 'wide'
    ? ''
    : user?.pref_layout === 'medium'
      ? 'max-w-[1600px]'
      : 'max-w-6xl';
  // 'blue' theme: sidebar in the brand color, white logo, light nav items.
  const isBlue = user?.pref_theme === 'blue';
  const sidebarBg = isBlue ? 'bg-[#1D4ED8] border-r border-[#1a44bd]' : 'bg-white border-r border-gray-200';
  const sidebarDivider = isBlue ? 'border-[rgba(255,255,255,0.18)]' : 'border-gray-200';
  const navItems = [
    { to: '/app', icon: House, label: t('sidebar.dashboard'), end: true },
    { to: '/app/campaigns', icon: Megaphone, label: t('sidebar.campaigns') },
    { to: '/app/suggestions', icon: Lightbulb, label: t('sidebar.suggestions') },
    { to: '/app/insights', icon: ChartBar, label: t('sidebar.audit') },
    ...(hasBudgetPlanner ? [{ to: '/app/planner', icon: Calculator, label: t('sidebar.budgetPlanner') }] : []),
    ...(isAgency ? [{ to: '/app/agency', icon: Buildings, label: t('sidebar.agencyMode') }] : []),
    { to: '/app/guides', icon: BookOpen, label: t('sidebar.guides') },
    { to: '/app/test-shops', icon: Storefront, label: t('sidebar.testShops') },
    { to: '/app/activity', icon: Pulse, label: t('sidebar.activity') },
    { to: '/app/settings', icon: Gear, label: t('sidebar.settings') },
  ];

  const isActive = (to, end) => {
    if (end) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  const renderNavItems = (isMobile = false) => (
    <div className="flex-1 px-2 py-3 space-y-0.5">
      {navItems.map(item => {
        const Icon = item.icon;
        const active = isActive(item.to, item.end);
        const showLabel = !collapsed || isMobile;

        const navLink = (
          <NavLink
            key={item.to}
            component={RouterNavLink}
            to={item.to}
            end={item.end}
            onClick={() => setMobileMenuOpen(false)}
            active={active}
            label={showLabel ? item.label : undefined}
            leftSection={<Icon size={showLabel ? 24 : 26} weight={active ? 'fill' : 'regular'} color={isBlue ? (active ? '#ffffff' : 'rgba(255,255,255,0.85)') : (active ? 'var(--mantine-color-brand-5)' : undefined)} />}
            styles={{
              root: {
                borderRadius: showLabel ? 'var(--mantine-radius-md)' : 'var(--mantine-radius-sm)',
                marginBottom: 2,
                padding: showLabel ? '10px 12px' : '10px',
                aspectRatio: showLabel ? undefined : '1',
                justifyContent: showLabel ? 'flex-start' : 'center',
                fontWeight: active ? 600 : 500,
                fontSize: '0.875rem',
                // Light theme: black text, brand icon. Blue theme: white text on indigo.
                color: isBlue ? (active ? '#ffffff' : 'rgba(255,255,255,0.88)') : '#1a1a2e',
                backgroundColor: active
                  ? (isBlue ? 'rgba(255,255,255,0.16)' : 'var(--mantine-color-gray-0)')
                  : 'transparent',
                '&:hover': {
                  backgroundColor: isBlue ? 'rgba(255,255,255,0.12)' : 'var(--mantine-color-gray-0)',
                  color: isBlue ? '#ffffff' : 'var(--mantine-color-dark-7)',
                },
              },
              label: {
                fontSize: '0.875rem',
              },
              section: {
                marginRight: showLabel ? 10 : 0,
              },
            }}
          />
        );

        if (collapsed && !isMobile) {
          return (
            <Tooltip key={item.to} label={item.label} position="right" withArrow transitionProps={{ transition: 'fade', duration: 150 }}>
              <div>{navLink}</div>
            </Tooltip>
          );
        }

        return navLink;
      })}
    </div>
  );

  const renderSupportButton = (isMobile = false) => {
    const showLabel = !collapsed || isMobile;
    const btn = (
      <NavLink
        component="button"
        onClick={() => { setShowSupport(true); setMobileMenuOpen(false); }}
        label={showLabel ? t('sidebar.contact') : undefined}
        leftSection={<ChatCircleText size={showLabel ? 24 : 26} color={isBlue ? 'rgba(255,255,255,0.85)' : 'var(--mantine-color-brand-5)'} />}
        styles={{
          root: {
            borderRadius: showLabel ? 'var(--mantine-radius-md)' : 'var(--mantine-radius-sm)',
            padding: showLabel ? '10px 12px' : '10px',
            justifyContent: showLabel ? 'flex-start' : 'center',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: isBlue ? 'rgba(255,255,255,0.88)' : '#1a1a2e',
            '&:hover': { backgroundColor: isBlue ? 'rgba(255,255,255,0.12)' : 'var(--mantine-color-gray-0)' },
          },
          label: { fontSize: '0.875rem' },
          section: { marginRight: showLabel ? 10 : 0 },
        }}
      />
    );
    return (
      <div className="px-2 pb-1">
        {collapsed && !isMobile ? (
          <Tooltip label={t('sidebar.contact')} position="right" withArrow><div>{btn}</div></Tooltip>
        ) : btn}
      </div>
    );
  };

  const renderUserSection = (isMobile = false) => {
    const showFull = !collapsed || isMobile;

    return (
      <div className={`border-t ${sidebarDivider} ${showFull ? 'px-3 py-3' : 'py-3 flex justify-center'}`}>
        <Menu shadow="md" width={200} position="top-start" withArrow>
          <Menu.Target>
            <button
              className={`flex items-center w-full rounded-lg transition-colors ${isBlue ? 'hover:bg-white/10' : 'hover:bg-gray-50'} ${showFull ? 'gap-2.5 px-2 py-2' : 'justify-center p-2'}`}
            >
              <div className={`w-7 h-7 rounded-full ${isBlue ? 'bg-white/20' : 'bg-indigo-100'} flex items-center justify-center flex-shrink-0`}>
                <User size={14} weight="bold" className={isBlue ? 'text-white' : 'text-indigo-600'} />
              </div>
              {showFull && (
                <span className={`text-xs ${isBlue ? 'text-indigo-100' : 'text-gray-500'} truncate flex-1 text-left`}>
                  {user?.email}
                </span>
              )}
            </button>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Label>
              <Text size="xs" c="dimmed" truncate>{user?.email}</Text>
            </Menu.Label>
            <Menu.Item
              leftSection={<Gear size={14} />}
              onClick={() => navigate('/app/settings')}
            >
              {t('sidebar.settings')}
            </Menu.Item>
            <Divider my={4} />
            <Menu.Item
              color="red"
              leftSection={<SignOut size={14} />}
              onClick={handleLogout}
            >
              {t('sidebar.logout')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      {showOnboarding && <Onboarding onComplete={handleOnboardingComplete} />}
      <SupportModal opened={showSupport} onClose={() => setShowSupport(false)} />

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 h-[56px] flex items-center justify-between px-4">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Menu"
        >
          {mobileMenuOpen ? <X size={20} /> : <List size={20} />}
        </ActionIcon>
        <div className="cursor-pointer" onClick={() => navigate('/app')}>
          <Logo className="h-7" />
        </div>
        <TaskNotifications />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/20" onClick={() => setMobileMenuOpen(false)} />
          <aside className={`absolute left-0 top-0 bottom-0 w-64 ${sidebarBg} shadow-xl flex flex-col`}>
            <div className={`flex items-center justify-between h-[56px] px-4 border-b ${sidebarDivider}`}>
              <Logo className="h-7" variant={isBlue ? 'white' : undefined} />
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => setMobileMenuOpen(false)}
                aria-label={t('sidebar.closeMenu')}
              >
                <X size={18} />
              </ActionIcon>
            </div>

            {renderNavItems(true)}
            {renderSupportButton(true)}
            {renderUserSection(true)}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className={`hidden md:flex ${collapsed ? 'w-16' : 'w-60'} fixed left-0 top-0 bottom-0 ${sidebarBg} shadow-[2px_0_8px_rgba(0,0,0,0.04)] flex-col h-screen transition-all duration-150 z-40`}>
        {/* Logo */}
        <div className={`flex items-center h-[56px] ${collapsed ? 'justify-center' : 'px-4'} border-b ${sidebarDivider}`}>
          {!collapsed && (
            <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => navigate('/app')}>
              <Logo className="h-8" variant={isBlue ? 'white' : undefined} />
              {isAgency && (
                <Badge variant={isBlue ? 'white' : 'light'} color="brand" size="xs" radius="sm" styles={{ root: { textTransform: 'none', flexShrink: 0 } }}>
                  {t('sidebar.agencyBadge')}
                </Badge>
              )}
            </div>
          )}
          {collapsed && (
            <div className="relative cursor-pointer" onClick={() => navigate('/app')}>
              <img src={isBlue ? '/logo_icon_white.svg' : '/logo_icon_only.svg'} alt="Droid" className="h-7 w-7" />
            </div>
          )}
        </div>

        {/* Collapse/expand toggle */}
        <Tooltip label={t(collapsed ? 'sidebar.expandMenu' : 'sidebar.collapseMenu')} position="right" withArrow>
          <ActionIcon
            variant="default"
            size={24}
            onClick={() => setCollapsed(!collapsed)}
            className="absolute top-[18px] -right-3 z-50 sidebar-toggle"
            styles={{
              root: {
                backgroundColor: 'white',
                border: '1px solid var(--mantine-color-gray-3)',
                color: 'var(--mantine-color-brand-5)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              },
            }}
          >
            {collapsed ? <CaretRight size={14} weight="bold" /> : <CaretLeft size={14} weight="bold" />}
          </ActionIcon>
        </Tooltip>

        {renderNavItems(false)}
        {renderSupportButton(false)}
        {renderUserSection(false)}
      </aside>

      {/* Main content */}
      <main className={`mt-[56px] md:mt-0 min-h-screen transition-all duration-150 ${collapsed ? 'md:ml-16' : 'md:ml-60'}`}>
        <div className="hidden md:block sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className={`${layoutMaxW} mx-auto px-6 h-[56px] flex items-center gap-4`}>
            <GlobalSearch />
            <div className="ml-auto flex items-center gap-2">
              <AccountSwitcher platform="google_ads" />
              <AccountSwitcher platform="meta_ads" />
              <LanguageSwitcher />
              <TaskNotifications />
            </div>
          </div>
        </div>
        {subEnding && (
          <div className="bg-amber-50 border-b border-amber-200">
            <div className={`${layoutMaxW} mx-auto px-6 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2`}>
              <Text size="sm" c="#92400e" style={{ flex: 1, minWidth: 220 }}>
                {t('subscription.endingBar', { date: cancelAtLabel })}
              </Text>
              <Button
                size="xs"
                color="brand"
                loading={resumingSub}
                onClick={handleResumeSubscription}
              >
                {resumingSub ? t('subscription.resuming') : t('subscription.resume')}
              </Button>
            </div>
          </div>
        )}
        <div className={`${layoutMaxW} mx-auto`}>
          <ErrorBoundary routeKey={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
